import React, { useState, useEffect } from 'react';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { applyTheme, getSavedTheme } from '../../../utils/theme';

const AppSettings = ({ onChange, onDeleteAccount }) => {
  const [appSettings, setAppSettings] = useState({
    theme: 'system',
    language: 'en',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12h'
  });

  const [isExporting, setIsExporting] = useState(false);

  // Initialize theme from localStorage on mount
  useEffect(() => {
    const savedTheme = getSavedTheme();
    setAppSettings(prev => ({ ...prev, theme: savedTheme }));
  }, []);

  const handleSelectChange = (name, value) => {
    setAppSettings(prev => ({ ...prev, [name]: value }));
    
    // Apply theme immediately when changed
    if (name === 'theme') {
      applyTheme(value);
    }
    
    onChange?.();
  };

  const handleExportData = () => {
    setIsExporting(true);
    setTimeout(() => {
      setIsExporting(false);
      alert('Your data export has been prepared and will be sent to your email shortly.');
    }, 2000);
  };

  const themeOptions = [
    { value: 'light', label: 'Light', description: 'Light color scheme' },
    { value: 'dark', label: 'Dark', description: 'Dark color scheme' },
    { value: 'system', label: 'System', description: 'Follow system preference' }
  ];

  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Español' },
    { value: 'fr', label: 'Français' },
    { value: 'de', label: 'Deutsch' },
    { value: 'it', label: 'Italiano' },
    { value: 'pt', label: 'Português' }
  ];

  const dateFormatOptions = [
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY', description: '12/31/2024' },
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY', description: '31/12/2024' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD', description: '2024-12-31' }
  ];

  const timeFormatOptions = [
    { value: '12h', label: '12-hour', description: '2:30 PM' },
    { value: '24h', label: '24-hour', description: '14:30' }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
          Appearance
        </h3>
        <Select
          label="Theme"
          value={appSettings?.theme}
          onChange={(value) => handleSelectChange('theme', value)}
          options={themeOptions}
          description="Choose your preferred color scheme"
        />
      </div>

      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
          Localization
        </h3>
        <div className="space-y-4">
          <Select
            label="Language"
            value={appSettings?.language}
            onChange={(value) => handleSelectChange('language', value)}
            options={languageOptions}
            description="Select your preferred language"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Date Format"
              value={appSettings?.dateFormat}
              onChange={(value) => handleSelectChange('dateFormat', value)}
              options={dateFormatOptions}
            />
            <Select
              label="Time Format"
              value={appSettings?.timeFormat}
              onChange={(value) => handleSelectChange('timeFormat', value)}
              options={timeFormatOptions}
            />
          </div>
        </div>
      </div>

      <div className="border-t border-border pt-6">
        <div className="flex items-start space-x-3 mb-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="Download" size={20} color="var(--color-primary)" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-heading font-semibold text-foreground">
              Data Export
            </h3>
            <p className="text-sm text-muted-foreground mt-1 mb-4">
              Download a copy of all your personal data including mood history, preferences, and encounter records
            </p>
            <Button
              variant="outline"
              onClick={handleExportData}
              loading={isExporting}
              iconName="Download"
            >
              Export My Data
            </Button>
          </div>
        </div>
      </div>

      <div className="border-t border-border pt-6">
        <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-destructive/20 flex items-center justify-center">
              <Icon name="AlertTriangle" size={20} color="var(--color-destructive)" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-heading font-semibold text-foreground mb-1">
                Danger Zone
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Once you delete your account, there is no going back. All your data will be permanently removed and cannot be recovered.
              </p>
              <Button
                variant="destructive"
                onClick={onDeleteAccount}
                iconName="Trash2"
              >
                Delete Account
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
          About
        </h3>
        <div className="space-y-2 text-sm text-muted-foreground">
          <div className="flex items-center justify-between py-2">
            <span>Version</span>
            <span className="font-medium text-foreground">1.0.0</span>
          </div>
          <div className="flex items-center justify-between py-2">
            <span>Last Updated</span>
            <span className="font-medium text-foreground">February 2026</span>
          </div>
          <div className="flex items-center space-x-4 py-2">
            <a href="#" className="text-primary hover:underline">Terms of Service</a>
            <a href="#" className="text-primary hover:underline">Privacy Policy</a>
            <a href="#" className="text-primary hover:underline">Support</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppSettings;